import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectIsAuthenticated } from '@/features/auth/authSlice';
import {
  loadWalletSummary,
  loadWalletTransactions,
  selectWalletSummary,
  selectWalletTransactions,
} from '@/features/wallet/walletSlice';
import { selectSelectedCurrency } from '@/features/preferences/preferencesSlice';
import { currencyOptionByCode } from '@/constants/currency';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { createWalletTopUpCheckout, fetchLiqPayExchangeRate, WalletTopUpPayload } from '@/services/walletService';
import { prepareLiqPayCheckoutTarget, submitLiqPayForm } from '@/utils/liqpay';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ArrowUpRight, RefreshCw } from 'lucide-react';
import { analytics } from '@/services/analytics';
import PaymentInstructionsModal from '@/components/liqpay/PaymentInstructionsModal';
import { logging } from '@/services/logging';
import { resolveLiqPayLanguage } from '@/utils/liqpayLanguage';

const formatCurrency = (value: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    logging.warn?.('Failed to format currency', { error, currency, value });
    return `${currency} ${value.toFixed(2)}`;
  }
};

const formatDate = (iso: string) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const formatCompactDateTime = (iso: string) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const formatEnumLabelFallback = (value: string): string => (
  value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase())
);

const roundCurrencyValue = (value: number): number => Math.round(value * 100) / 100;

const MIN_TOP_UP_USD = 1;
const MAX_TOP_UP_USD = 1000;
const DEFAULT_TOP_UP_USD = 50;
const LEDGER_CURRENCY = 'USD' as const;
const MOBILE_TRANSACTIONS_PAGE_SIZE = 5;
const DESKTOP_TRANSACTIONS_PAGE_SIZE = 20;

const LIQPAY_SUPPORTED_CURRENCIES = ['USD', 'EUR', 'UAH'] as const;

const WalletPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const summaryState = useAppSelector(selectWalletSummary);
  const transactionsState = useAppSelector(selectWalletTransactions);
  const selectedGlobalCurrency = useAppSelector(selectSelectedCurrency);

  const [topUpLedgerAmount, setTopUpLedgerAmount] = useState<number>(DEFAULT_TOP_UP_USD);
  const [topUpAmountInput, setTopUpAmountInput] = useState<string>(DEFAULT_TOP_UP_USD.toFixed(2));
  const [isTopUpLoading, setIsTopUpLoading] = useState(false);
  const [topUpError, setTopUpError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [pendingPayment, setPendingPayment] = useState<{ id: string; amount: number; currency: string } | null>(null);
  const [isWaitingModalOpen, setIsWaitingModalOpen] = useState(false);
  const [isRefreshingPayment, setIsRefreshingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const pollIntervalRef = useRef<number | null>(null);
  const [liqPayCurrency, setLiqPayCurrency] = useState<string>('USD');
  const [exchangeRate, setExchangeRate] = useState<{ rate: number; status: 'idle' | 'loading' | 'success' | 'error'; error?: string | null }>({
    rate: 1,
    status: 'idle',
    error: null,
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 639.98px)');
    const updateViewport = () => {
      setIsMobileViewport(mediaQuery.matches);
    };

    updateViewport();
    mediaQuery.addEventListener('change', updateViewport);
    return () => mediaQuery.removeEventListener('change', updateViewport);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    dispatch(loadWalletSummary());
  }, [dispatch, isAuthenticated, navigate]);

  const transactionsPageSize = isMobileViewport ? MOBILE_TRANSACTIONS_PAGE_SIZE : DESKTOP_TRANSACTIONS_PAGE_SIZE;

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    dispatch(loadWalletTransactions({ page, pageSize: transactionsPageSize }));
  }, [dispatch, isAuthenticated, page, transactionsPageSize]);

  const summary = summaryState.data;
  const transactions = transactionsState.data;
  const pagination = transactionsState.pagination;
  const summaryCurrency = LEDGER_CURRENCY;
  const getTransactionTypeLabel = useCallback(
    (value: string): string => {
      const normalized = value.trim().toUpperCase();
      const keyByValue: Record<string, string> = {
        ORDER_CHARGE: 'orderCharge',
        WALLET_TOPUP: 'walletTopUp',
        ORDER_REFUND: 'orderRefund',
        WALLET_PAYOUT: 'walletPayout',
        ORDER_REFERRAL_COMMISSION: 'orderReferralCommission',
      };
      const key = keyByValue[normalized];
      return key
        ? t(`wallet.enums.transactionType.${key}`, formatEnumLabelFallback(value))
        : formatEnumLabelFallback(value);
    },
    [t],
  );
  const getTransactionSourceLabel = useCallback(
    (value: string): string => {
      const normalized = value.trim().toUpperCase();
      const keyByValue: Record<string, string> = {
        ORDER_SUMMARY: 'orderSummary',
        WALLET_UI: 'walletUi',
      };
      const key = keyByValue[normalized];
      return key
        ? t(`wallet.enums.transactionSource.${key}`, formatEnumLabelFallback(value))
        : formatEnumLabelFallback(value);
    },
    [t],
  );
  const getTransactionStatusLabel = useCallback(
    (value: string): string => {
      const normalized = value.trim().toUpperCase();
      const keyByValue: Record<string, string> = {
        COMPLETED: 'completed',
        PENDING: 'pending',
        FAILED: 'failed',
      };
      const key = keyByValue[normalized];
      return key
        ? t(`wallet.enums.status.${key}`, normalized)
        : formatEnumLabelFallback(value);
    },
    [t],
  );
  const liqPayOptions = useMemo(
    () => LIQPAY_SUPPORTED_CURRENCIES.map(code => {
      const option = currencyOptionByCode[code];
      const label = option ? `${option.code} · ${option.label}` : code;
      return { value: code, label };
    }),
    [],
  );

  const effectiveRate = useMemo(() => {
    if (liqPayCurrency === LEDGER_CURRENCY) {
      return 1;
    }
    if (exchangeRate.status !== 'success' || !Number.isFinite(exchangeRate.rate) || exchangeRate.rate <= 0) {
      return null;
    }
    return exchangeRate.rate;
  }, [exchangeRate.rate, exchangeRate.status, liqPayCurrency]);

  const isRateReady = liqPayCurrency === LEDGER_CURRENCY || effectiveRate != null;

  const selectedCurrencyMin = useMemo(() => {
    if (liqPayCurrency === LEDGER_CURRENCY) {
      return MIN_TOP_UP_USD;
    }
    if (!isRateReady || effectiveRate == null) {
      return MIN_TOP_UP_USD;
    }
    return roundCurrencyValue(MIN_TOP_UP_USD * effectiveRate);
  }, [effectiveRate, isRateReady, liqPayCurrency]);

  const selectedCurrencyMax = useMemo(() => {
    if (liqPayCurrency === LEDGER_CURRENCY) {
      return MAX_TOP_UP_USD;
    }
    if (!isRateReady || effectiveRate == null) {
      return MAX_TOP_UP_USD;
    }
    return roundCurrencyValue(MAX_TOP_UP_USD * effectiveRate);
  }, [effectiveRate, isRateReady, liqPayCurrency]);

  const topUpRangeLabel = useMemo(() => {
    if (!isRateReady && liqPayCurrency !== LEDGER_CURRENCY) {
      const minUsd = formatCurrency(MIN_TOP_UP_USD, LEDGER_CURRENCY);
      const maxUsd = formatCurrency(MAX_TOP_UP_USD, LEDGER_CURRENCY);
      return t('wallet.topUpRangePending', 'Min {{min}} · Max {{max}} (rate pending)', { min: minUsd, max: maxUsd });
    }
    const minLabel = liqPayCurrency === LEDGER_CURRENCY
      ? formatCurrency(MIN_TOP_UP_USD, LEDGER_CURRENCY)
      : formatCurrency(selectedCurrencyMin, liqPayCurrency);
    const maxLabel = liqPayCurrency === LEDGER_CURRENCY
      ? formatCurrency(MAX_TOP_UP_USD, LEDGER_CURRENCY)
      : formatCurrency(selectedCurrencyMax, liqPayCurrency);
    return t('wallet.topUpRangeCurrency', 'Min {{min}} · Max {{max}}', { min: minLabel, max: maxLabel });
  }, [isRateReady, liqPayCurrency, selectedCurrencyMax, selectedCurrencyMin, t]);

  useEffect(() => {
    const defaultCurrency = LIQPAY_SUPPORTED_CURRENCIES.includes(selectedGlobalCurrency as typeof LIQPAY_SUPPORTED_CURRENCIES[number])
      ? selectedGlobalCurrency
      : 'USD';
    setLiqPayCurrency(defaultCurrency);
  }, [selectedGlobalCurrency]);

  useEffect(() => {
    let cancelled = false;
    if (!summary) {
      setExchangeRate({ rate: 1, status: 'success', error: null });
      return () => {
        cancelled = true;
      };
    }
    if (liqPayCurrency === summaryCurrency) {
      setExchangeRate({ rate: 1, status: 'success', error: null });
      return () => {
        cancelled = true;
      };
    }
    setExchangeRate(prev => ({ ...prev, status: 'loading', error: null }));
    fetchLiqPayExchangeRate(summaryCurrency, liqPayCurrency)
      .then(result => {
        if (cancelled) return;
        setExchangeRate({ rate: result.rate, status: 'success', error: null });
      })
      .catch(error => {
        if (cancelled) return;
        logging.error('Failed to fetch wallet exchange rate', { error });
        const message = error instanceof Error ? error.message : t('wallet.exchangeRateError', 'Unable to load exchange rate. Showing USD amounts.');
        setExchangeRate({ rate: 1, status: 'error', error: message });
      });
    return () => {
      cancelled = true;
    };
  }, [liqPayCurrency, summary, summaryCurrency, t]);

  const formatConverted = useCallback(
    (valueUsd: number) => {
      if (liqPayCurrency === LEDGER_CURRENCY) {
        return formatCurrency(valueUsd, LEDGER_CURRENCY);
      }
      if (!isRateReady || effectiveRate == null) {
        return formatCurrency(valueUsd, LEDGER_CURRENCY);
      }
      return formatCurrency(valueUsd * effectiveRate, liqPayCurrency);
    },
    [effectiveRate, isRateReady, liqPayCurrency],
  );

  const topUpLedgerAmountRef = useRef<number>(topUpLedgerAmount);

  useEffect(() => {
    topUpLedgerAmountRef.current = topUpLedgerAmount;
  }, [topUpLedgerAmount]);

  useEffect(() => {
    const ledgerAmount = topUpLedgerAmountRef.current;
    if (!isRateReady) {
      setTopUpAmountInput(roundCurrencyValue(ledgerAmount).toFixed(2));
      return;
    }
    const nextAmount = liqPayCurrency === LEDGER_CURRENCY
      ? roundCurrencyValue(ledgerAmount)
      : roundCurrencyValue(ledgerAmount * (effectiveRate ?? 1));
    setTopUpAmountInput(nextAmount.toFixed(2));
  }, [effectiveRate, isRateReady, liqPayCurrency]);

  const handleCurrencySelect = useCallback((value: string) => {
    setLiqPayCurrency(value);
    setTopUpError(null);
    analytics.track('select: wallet.currency_change', { currency: value });
  }, []);

  const validateTopUpAmount = useCallback((rawValue: string) => {
    if (!isRateReady) {
      return {
        isValid: false as const,
        error: t('wallet.exchangeRateRequired', 'Unable to convert amount. Please refresh the exchange rate.'),
      };
    }

    const normalizedInput = rawValue.replace(',', '.').trim();
    const parsed = Number.parseFloat(normalizedInput);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return {
        isValid: false as const,
        error: t('wallet.invalidAmountCurrency', 'Enter a valid amount in {{currency}}', { currency: liqPayCurrency }),
      };
    }

    const selectedAmount = roundCurrencyValue(parsed);
    const ledgerAmount = liqPayCurrency === LEDGER_CURRENCY
      ? selectedAmount
      : roundCurrencyValue(selectedAmount / (effectiveRate ?? 1));

    if (ledgerAmount < MIN_TOP_UP_USD || ledgerAmount > MAX_TOP_UP_USD) {
      const minLabel = liqPayCurrency === LEDGER_CURRENCY || !isRateReady
        ? formatCurrency(MIN_TOP_UP_USD, LEDGER_CURRENCY)
        : formatCurrency(selectedCurrencyMin, liqPayCurrency);
      const maxLabel = liqPayCurrency === LEDGER_CURRENCY || !isRateReady
        ? formatCurrency(MAX_TOP_UP_USD, LEDGER_CURRENCY)
        : formatCurrency(selectedCurrencyMax, liqPayCurrency);

      return {
        isValid: false as const,
        error: t('wallet.topUpRangeError', 'Enter an amount between {{min}} and {{max}}.', { min: minLabel, max: maxLabel }),
      };
    }

    return {
      isValid: true as const,
      ledgerAmount,
      selectedAmount,
    };
  }, [effectiveRate, isRateReady, liqPayCurrency, selectedCurrencyMax, selectedCurrencyMin, t]);

  const topUpAmountValidation = useMemo(() => validateTopUpAmount(topUpAmountInput), [topUpAmountInput, validateTopUpAmount]);

  const handleTopUpAmountChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setTopUpAmountInput(value);
    setTopUpError(null);
  }, []);

  const handleTopUpAmountBlur = useCallback(() => {
    const validation = validateTopUpAmount(topUpAmountInput);
    if (!validation.isValid) {
      setTopUpError(validation.error);
      return;
    }

    setTopUpLedgerAmount(validation.ledgerAmount);
    setTopUpAmountInput(validation.selectedAmount.toFixed(2));
    setTopUpError(null);
  }, [topUpAmountInput, validateTopUpAmount]);

  const convertedBalance = useMemo(() => (summary ? formatConverted(summary.balance) : formatConverted(0)), [formatConverted, summary]);
  const convertedDebits = useMemo(() => (summary ? formatConverted(summary.totalDebits) : formatConverted(0)), [formatConverted, summary]);
  const convertedCredits = useMemo(() => (summary ? formatConverted(summary.totalCredits) : formatConverted(0)), [formatConverted, summary]);

  const handleRefresh = () => {
    if (!isAuthenticated) return;
    dispatch(loadWalletSummary());
    dispatch(loadWalletTransactions({ page, pageSize: transactionsPageSize }));
    analytics.track('button: wallet.refresh', { page });
  };

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current != null) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const clearPendingPayment = useCallback(() => {
    setPendingPayment(null);
    setPaymentStatus(null);
  }, []);

  const handleModalClose = useCallback(() => {
    stopPolling();
    setIsWaitingModalOpen(false);
    clearPendingPayment();
  }, [clearPendingPayment, stopPolling]);

  const fetchPaymentStatus = useCallback(async (paymentId: string) => {
    setIsRefreshingPayment(true);
    setPaymentError(null);
    try {
      const response = await dispatch(loadWalletTransactions({ page: 1, pageSize: transactionsPageSize })).unwrap();
      const match = response.data.find(txn => txn.id === paymentId);
      if (match) {
        setPaymentStatus(match.status);
        if (match.status === 'COMPLETED') {
          analytics.track('timer: wallet.topup_completed', { amount: match.amount, currency: match.currency });
          await dispatch(loadWalletSummary());
          stopPolling();
          setIsWaitingModalOpen(false);
          clearPendingPayment();
          return;
        }
      } else {
        // Pending payment not yet available via transactions endpoint.
      }
    } catch (error) {
      logging.error('Failed to refresh wallet payment status', { error });
      setPaymentError(t('liqpay.errors.refresh'));
    } finally {
      setIsRefreshingPayment(false);
    }
  }, [clearPendingPayment, dispatch, stopPolling, t]);

  const refreshPaymentStatus = useCallback(async () => {
    if (!pendingPayment) {
      return;
    }
    await fetchPaymentStatus(pendingPayment.id);
  }, [fetchPaymentStatus, pendingPayment]);

  useEffect(() => {
    if (!isWaitingModalOpen || !pendingPayment) {
      return;
    }
    void fetchPaymentStatus(pendingPayment.id);
    stopPolling();
    pollIntervalRef.current = window.setInterval(() => {
      void fetchPaymentStatus(pendingPayment.id);
    }, 5000);
    return () => {
      stopPolling();
    };
  }, [fetchPaymentStatus, isWaitingModalOpen, pendingPayment, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const handleTopUp = async () => {
    setTopUpError(null);
    const validation = validateTopUpAmount(topUpAmountInput);
    if (!validation.isValid) {
      setTopUpError(validation.error);
      return;
    }

    const ledgerAmount = validation.ledgerAmount;
    const originalAmount = validation.selectedAmount;
    setTopUpLedgerAmount(ledgerAmount);

    const checkoutTarget = prepareLiqPayCheckoutTarget();
    setIsTopUpLoading(true);
    try {
      analytics.track('button: wallet.topup_attempt', {
        amountLedger: ledgerAmount,
        amountOriginal: originalAmount,
        currency: liqPayCurrency,
      });
      const payload: WalletTopUpPayload = {
        amount: originalAmount,
        currency: liqPayCurrency,
        language: resolveLiqPayLanguage(i18n.language),
      };
      const checkout = await createWalletTopUpCheckout(payload);
      submitLiqPayForm(checkout.data, checkoutTarget);
      analytics.track('button: wallet.topup_checkout_generated', {
        amountLedger: ledgerAmount,
        amountOriginal: originalAmount,
        currency: liqPayCurrency,
      });
      if (checkout.paymentId) {
        setPendingPayment({ id: checkout.paymentId, amount: checkout.amount, currency: checkout.currency });
        setPaymentStatus(checkout.status ?? 'PENDING');
        setPaymentError(null);
        setIsWaitingModalOpen(true);
        analytics.track('button: wallet.topup_waiting_modal_open', {
          paymentId: checkout.paymentId,
          amount: checkout.amount,
          currency: checkout.currency,
        });
        void fetchPaymentStatus(checkout.paymentId);
      }
    } catch (error) {
      checkoutTarget.handle?.close();
      logging.error('Failed to create LiqPay checkout', { error });
      analytics.track('button: wallet.topup_checkout_failed', { currency: liqPayCurrency });
      setTopUpError(t('wallet.topUpFailed', 'Failed to initiate top-up. Please try again.'));
    } finally {
      setIsTopUpLoading(false);
    }
  };

  const goToPrevious = () => {
    if (page > 1) {
      setPage(current => {
        const next = Math.max(1, current - 1);
        analytics.track('button: wallet.pagination_prev', { from: current, to: next });
        return next;
      });
    }
  };

  const goToNext = () => {
    if (pagination.totalPages === 0) return;
    setPage(current => {
      const next = Math.min(pagination.totalPages, current + 1);
      analytics.track('button: wallet.pagination_next', { from: current, to: next });
      return next;
    });
  };

  const isSummaryLoading = summaryState.status === 'loading';
  const isTransactionsLoading = transactionsState.status === 'loading';

  return (
    <div className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-background via-muted/40 to-background py-10">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              {t('wallet.title', 'Wallet & Balance')}
            </h1>
            <p className="mt-1 text-muted-foreground">
              {t('wallet.subtitle', 'Track your Tulasi credit, review debits, and add funds instantly via LiqPay.')}
            </p>
          </div>
          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-end sm:gap-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Label htmlFor="wallet-currency" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('orderSummary.liqPayCurrencyLabel', 'Payment currency')}
                </Label>
                {exchangeRate.status === 'loading' ? <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" /> : null}
              </div>
              <Select value={liqPayCurrency} onValueChange={handleCurrencySelect}>
                <SelectTrigger id="wallet-currency" className="w-48">
                  <SelectValue placeholder={t('orderSummary.liqPayCurrencyLabel', 'Payment currency')} />
                </SelectTrigger>
                <SelectContent>
                  {liqPayOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {exchangeRate.status === 'error' && exchangeRate.error ? (
                <p className="text-xs text-destructive/80">{exchangeRate.error}</p>
              ) : null}
              {exchangeRate.status === 'success' && liqPayCurrency !== summaryCurrency ? (
                <p className="text-xs text-muted-foreground">
                  {t('wallet.exchangeRateLabel', '1 {{base}} ≈ {{converted}}', {
                    base: summaryCurrency,
                    converted: formatCurrency(exchangeRate.rate, liqPayCurrency),
                  })}
                </p>
              ) : null}
            </div>
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={isSummaryLoading || isTransactionsLoading}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('wallet.refresh', 'Refresh')}
            </Button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          <Card className="bg-gradient-to-br from-emerald-500 via-emerald-400 to-teal-500 text-white shadow-lg">
            <CardHeader className="space-y-2">
              <CardTitle className="text-sm uppercase tracking-[0.3em] text-white/70">
                {t('wallet.currentBalance', 'Current balance')}
              </CardTitle>
              <div className="flex items-center justify-between gap-4">
                <div className="text-4xl font-bold">
                  {isSummaryLoading && !summary ? (
                    <Loader2 className="h-10 w-10 animate-spin" />
                  ) : (
                    convertedBalance
                  )}
                </div>
                <Badge variant="secondary" className="bg-white/20 text-xs uppercase tracking-wide text-white">
                  {liqPayCurrency}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-2 text-sm text-white/80">
              {summary?.lastActivityAt ? (
                <span>
                  {t('wallet.lastActivity', 'Last activity:')}{' '}
                  {formatDate(summary.lastActivityAt)}
                </span>
              ) : (
                <span>{t('wallet.noActivityYet', 'No wallet activity yet')}</span>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase">
                {t('wallet.debits', 'Total debits')}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold">
              {convertedDebits}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase">
                {t('wallet.credits', 'Total credits')}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold">
              {convertedCredits}
            </CardContent>
          </Card>
        </div>

        <Card className="border-dashed">
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-base font-semibold">
              <span>{t('wallet.topUpTitle', 'Add funds')}</span>
              <Badge variant="secondary" className="uppercase tracking-wide">
                {topUpRangeLabel}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Label htmlFor="wallet-amount" className="text-sm font-medium text-muted-foreground">
                {t('wallet.amountLabelCurrency', 'Amount ({{currency}})', { currency: liqPayCurrency })}
              </Label>
              <Input
                id="wallet-amount"
                type="text"
                inputMode="decimal"
                value={topUpAmountInput}
                onChange={handleTopUpAmountChange}
                onBlur={handleTopUpAmountBlur}
                className="mt-2"
              />
              <p
                className={`mt-2 min-h-5 text-sm ${topUpError ? 'text-destructive' : 'text-transparent'}`}
                aria-live="polite"
              >
                {topUpError ?? '\u00A0'}
              </p>
            </div>
            <Button
              onClick={handleTopUp}
              disabled={isTopUpLoading || !topUpAmountValidation.isValid}
              className="sm:mb-7 sm:w-56"
            >
              {isTopUpLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('wallet.generatingCheckout', 'Generating checkout...')}
                </>
              ) : (
                <>
                  <ArrowUpRight className="mr-2 h-4 w-4" />
                  {t('wallet.topUpCta', 'Top up via LiqPay')}
                </>
              )}
            </Button>
            {pendingPayment && (
              <p className="text-sm text-muted-foreground" data-testid="wallet-topup-status">
                {t('wallet.waitingStatus', 'Latest payment status: {{status}}', {
                  status: paymentStatus ?? 'PENDING',
                })}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">
              {t('wallet.transactionsTitle', 'Transaction history')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('wallet.table.date', 'Date')}</TableHead>
                    <TableHead>{t('wallet.table.type', 'Type')}</TableHead>
                    <TableHead className="hidden sm:table-cell">{t('wallet.table.source', 'Source')}</TableHead>
                    <TableHead>{t('wallet.table.status', 'Status')}</TableHead>
                    <TableHead className="text-right">{t('wallet.table.originalAmount', 'Original amount')}</TableHead>
                    <TableHead className="text-right">{t('wallet.table.amount', 'Amount')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isTransactionsLoading && transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                      </TableCell>
                    </TableRow>
                  ) : transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                        {t('wallet.noTransactions', 'No transactions yet. Top up to create your first entry.')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((txn) => {
                      const originalAmountRaw = txn.originalAmount ?? txn.original_amount ?? null;
                      let normalizedOriginalAmount: number | null = null;
                      if (typeof originalAmountRaw === 'string') {
                        const sanitized = originalAmountRaw.replace(/[^0-9.\-]/g, '');
                        const parsed = Number.parseFloat(sanitized);
                        normalizedOriginalAmount = Number.isFinite(parsed) ? parsed : null;
                      } else if (typeof originalAmountRaw === 'number') {
                        normalizedOriginalAmount = originalAmountRaw;
                      }
                      const originalCurrency = txn.originalCurrency ?? txn.original_currency ?? null;
                      const hasOriginalAmount = Boolean(
                        originalCurrency && normalizedOriginalAmount != null && Number.isFinite(normalizedOriginalAmount),
                      );

                      return (
                        <TableRow key={txn.id}>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatCompactDateTime(txn.createdAt)}
                          </TableCell>
                          <TableCell className="font-medium">
                            <span>{getTransactionTypeLabel(txn.transactionType)}</span>
                          </TableCell>
                          <TableCell className="hidden text-sm text-muted-foreground sm:table-cell">
                            <span>{getTransactionSourceLabel(txn.transactionSource)}</span>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={txn.status === 'COMPLETED' ? 'default' : txn.status === 'PENDING' ? 'secondary' : 'destructive'}
                            >
                              {getTransactionStatusLabel(txn.status)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right text-sm text-muted-foreground">
                            {hasOriginalAmount && originalCurrency
                              ? formatCurrency(Math.abs(normalizedOriginalAmount ?? 0), originalCurrency)
                              : t('wallet.originalAmountUnavailable', '—')}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            <span
                              className={
                                txn.direction === 'DEBIT'
                                  ? 'text-emerald-600 dark:text-emerald-400'
                                  : 'text-destructive'
                              }
                            >
                              {txn.direction === 'DEBIT' ? '+' : '-'}{' '}
                              {formatCurrency(Math.abs(txn.amount), summaryCurrency)}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                {pagination.total === 0
                  ? t('wallet.pagination.empty', 'No entries yet')
                  : t('wallet.pagination.info', 'Page {{page}} of {{totalPages}}', {
                      page: pagination.page,
                      totalPages: pagination.totalPages || 1,
                    })}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={goToPrevious} disabled={page <= 1}>
                  {t('wallet.pagination.prev', 'Previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNext}
                  disabled={pagination.totalPages !== 0 && page >= pagination.totalPages}
                >
                  {t('wallet.pagination.next', 'Next')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <PaymentInstructionsModal
        open={isWaitingModalOpen && Boolean(pendingPayment)}
        onRefresh={refreshPaymentStatus}
        onClose={handleModalClose}
        isRefreshing={isRefreshingPayment}
        refreshLabel={t('liqpay.paymentModal.refreshButton')}
        refreshingLabel={t('liqpay.paymentModal.refreshingButton')}
        closeLabel={t('liqpay.paymentModal.closeButton')}
        title={t('liqpay.paymentModal.title')}
        description={pendingPayment
          ? t('liqpay.paymentModal.descriptionWithAmount', {
            defaultValue: t('liqpay.paymentModal.description'),
            amount: formatCurrency(pendingPayment.amount, pendingPayment.currency ?? 'USD'),
          })
          : t('liqpay.paymentModal.description')}
      />
      {paymentError ? (
        <p className="mt-2 text-sm text-destructive text-center" role="alert">
          {paymentError}
        </p>
      ) : null}
    </div>
  );
};

export default WalletPage;
